// =============================================================================
// Module Project - Repository: Tasks
// Seul fichier autorisé à importer supabase pour les opérations tâches
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Task } from '../types';
import { autoAllocateSoloTask } from './workloadRepository';

// ─────────────────────────────────────────────────────────────────────────────
// FETCH TASKS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchTasks(projectId: string): Promise<Task[]> {
  const { data, error } = await supabase
    .from('project_tasks')
    .select('*')
    .eq('project_id', projectId)
    .order('kanban_position', { ascending: true });

  if (error) throw error;
  return (data || []) as Task[];
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE TASK
// ─────────────────────────────────────────────────────────────────────────────
export async function createTask(
  orgId: string,
  projectId: string,
  taskData: Partial<Task>,
  userId: string
): Promise<Task> {
  // Validation: le titre est obligatoire et ne peut pas être vide
  const title = taskData.title?.trim();
  if (!title) {
    throw new Error('Le titre de la tâche est obligatoire');
  }

  const { data, error } = await supabase
    .from('project_tasks')
    .insert({
      org_id: orgId,
      project_id: projectId,
      title,
      description: taskData.description || null,
      status_code: taskData.status_code || 'todo',
      priority: taskData.priority || null,
      due_at: taskData.due_at || null,
      kanban_position: taskData.kanban_position || 0,
      parent_task_id: taskData.parent_task_id || null,
      milestone_id: taskData.milestone_id || null,
      created_by: userId,
      updated_by: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Task;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE TASK
// ─────────────────────────────────────────────────────────────────────────────
export async function updateTask(
  taskId: string,
  updates: Partial<Task>,
  userId?: string
): Promise<void> {
  const { data: updatedTask, error } = await supabase
    .from('project_tasks')
    .update({ ...updates, updated_by: userId } as any)
    .eq('id', taskId)
    .select('id, org_id')
    .single();

  if (error) throw error;

  // Synchronisation immédiate : pour une tâche solo, l'allocation doit suivre
  // l'estimation (et donc le reste à faire) sans attendre un refresh différé.
  if (updates.estimated_hours !== undefined && updatedTask?.org_id) {
    await autoAllocateSoloTask(taskId, updatedTask.org_id);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE TASK POSITION (batch for reordering)
// ─────────────────────────────────────────────────────────────────────────────
export async function updateTaskPosition(taskId: string, position: number): Promise<void> {
  const { error } = await supabase
    .from('project_tasks')
    .update({ kanban_position: position })
    .eq('id', taskId);

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE GANTT POSITIONS (batch for Gantt view custom ordering)
// ─────────────────────────────────────────────────────────────────────────────
export async function updateGanttPositions(
  taskIds: string[],
  userId?: string
): Promise<void> {
  // Update each task with its new gantt_position
  const updates = taskIds.map((taskId, index) => 
    supabase
      .from('project_tasks')
      .update({ gantt_position: index, updated_by: userId })
      .eq('id', taskId)
  );
  
  const results = await Promise.all(updates);
  const errors = results.filter(r => r.error);
  
  if (errors.length > 0) {
    throw errors[0].error;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE TASK
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteTask(taskId: string): Promise<void> {
  const { error } = await supabase
    .from('project_tasks')
    .delete()
    .eq('id', taskId);

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// MOVE TASK (via Edge Function)
// ─────────────────────────────────────────────────────────────────────────────
export async function moveTaskKanban(
  taskId: string,
  newStatusCode: string,
  newPosition: number
): Promise<void> {
  const { error } = await supabase.functions.invoke('move-task-kanban', {
    body: { task_id: taskId, new_status_code: newStatusCode, new_position: newPosition },
  });

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// AUDIT LOG
// ─────────────────────────────────────────────────────────────────────────────
export async function createAuditLog(
  orgId: string,
  userId: string,
  action: string,
  entityId: string,
  oldValues?: Record<string, unknown>,
  newValues?: Record<string, unknown>
): Promise<void> {
  await supabase.from('core_audit_logs').insert([{
    org_id: orgId,
    user_id: userId,
    action,
    entity_type: 'task',
    entity_id: entityId,
    old_values: oldValues as any,
    new_values: newValues as any,
  }]);
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH OPEN TASK COUNTS BY PROJECT (for sidebar)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchOpenTaskCountsByProject(
  orgId: string,
  projectIds: string[]
): Promise<Record<string, number>> {
  if (projectIds.length === 0) return {};

  const [orgStatusesResult, projectStatusesResult, tasksResult] = await Promise.all([
    supabase
      .from('project_task_statuses')
      .select('code, is_terminal')
      .eq('org_id', orgId)
      .is('project_id', null),
    supabase
      .from('project_task_statuses')
      .select('project_id, code, is_terminal')
      .eq('org_id', orgId)
      .in('project_id', projectIds),
    supabase
      .from('project_tasks')
      .select('project_id, status_code')
      .eq('org_id', orgId)
      .in('project_id', projectIds)
      .is('parent_task_id', null),
  ]);

  if (orgStatusesResult.error) throw orgStatusesResult.error;
  if (projectStatusesResult.error) throw projectStatusesResult.error;
  if (tasksResult.error) throw tasksResult.error;

  const orgStatusMap = new Map<string, boolean>(
    (orgStatusesResult.data || []).map((status) => [status.code, status.is_terminal === true])
  );

  const effectiveStatusMaps = new Map<string, Map<string, boolean>>();
  projectIds.forEach((projectId) => {
    effectiveStatusMaps.set(projectId, new Map(orgStatusMap));
  });

  (projectStatusesResult.data || []).forEach((status) => {
    if (!status.project_id) return;
    const projectStatusMap = effectiveStatusMaps.get(status.project_id) ?? new Map(orgStatusMap);
    projectStatusMap.set(status.code, status.is_terminal === true);
    effectiveStatusMaps.set(status.project_id, projectStatusMap);
  });

  const counts: Record<string, number> = {};
  (tasksResult.data || []).forEach((task: { project_id: string; status_code: string }) => {
    const isTerminal = effectiveStatusMaps.get(task.project_id)?.get(task.status_code) === true;
    if (!isTerminal) {
      counts[task.project_id] = (counts[task.project_id] || 0) + 1;
    }
  });

  return counts;
}

// ─────────────────────────────────────────────────────────────────────────────
// SEARCH TASKS WITH PROJECT (for command palette)
// ─────────────────────────────────────────────────────────────────────────────
export interface TaskSearchResult {
  id: string;
  title: string;
  project_id: string;
  project_name: string;
}

export async function searchTasksWithProject(
  orgId: string,
  query: string,
  limit: number = 10
): Promise<TaskSearchResult[]> {
  if (!query || query.trim().length === 0) return [];

  const { data, error } = await supabase
    .from('project_tasks')
    .select(`
      id,
      title,
      project_id,
      project_projects!inner (name)
    `)
    .eq('org_id', orgId)
    .is('parent_task_id', null)
    .ilike('title', `%${query}%`)
    .limit(limit);

  if (error) throw error;

  return (data || []).map((t: any) => ({
    id: t.id,
    title: t.title,
    project_id: t.project_id,
    project_name: t.project_projects?.name || '',
  }));
}
